<template>
  <PageContainer maxWidth="1300px">
    <!-- Summary -->
    <Card>
      <template #content>
        <VeeForm
          v-slot="{ handleSubmit }"
          :validation-schema="schema"
          as="div"
          @invalidSubmit="onInvalidSubmit"
        >
          <form
            @submit.prevent="handleSubmit($event, onSubmitOrganizationInfo)"
          >
            <h2 class="mt-0 text-xl">
              Organization
            </h2>
            <div class="grid">
              <div class="col-12">
                <BaseInput
                  v-model="organizationForm.name"
                  fieldId="name"
                  fieldName="name"
                  fieldLabel="Name"
                />
              </div>
            </div>
            <div class="flex justify-content-end">
              <Button
                type="submit"
                :loading="organizationFormIsSubmitting"
                label="Save"
              />
            </div>
          </form>
        </VeeForm>
      </template>
    </Card>

    <Card class="mt-4">
      <template #content>
        <div class="grid">
          <div class="col-12 sm:col sm:flex-grow-1">
            <h2 class="mt-0 text-xl">
              Users
            </h2>
          </div>
          <div class="col-12 sm:col sm:flex-grow-0">
            <Button
              class="w-full justify-content-center text-wrap-nowrap"
              severity="success"
              @click="addUserDialogData.visible = true"
            >
              Invite Admin
            </Button>
          </div>
        </div>

        <div class="mt-4">
          <DataTable
            :value="compiledUsers"
            dataKey="id"
          >
            <Column
              field="sanitizedEmail"
              header="Email"
            />
            <Column
              field="fname"
              header="First Name"
            />
            <Column
              field="lname"
              header="Last Name"
            />
            <Column
              field="role"
              header="Role"
            />
            <Column
              field="status"
              header="Status"
            >
              <template #body="slotProps">
                <Chip :class="slotProps.data.statusChipClass">{{ slotProps.data.status }}</Chip>
              </template>
            </Column>
            <Column
              class="w-1p"
            >
              <template #body="slotProps">
                <Button
                  icon="pi pi-ellipsis-v"
                  text
                  plain
                  aria-haspopup="true"
                  :aria-controls="`user-row-menu-${slotProps.data.id}`"
                  @click="(event) => this.$refs[`userRowMenu${slotProps.data.id}`].toggle(event)"
                />
                <Menu
                  :id="`user-row-menu-${slotProps.data.id}`"
                  class="right-align-menu"
                  :ref="`userRowMenu${slotProps.data.id}`"
                  popup
                  :model="userRowOptionsDictionary[slotProps.data.id]"
                />
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
    </Card>

    <!-- user dialogs -->

    <UserFormDialog
      v-model:visible="addUserDialogData.visible"
      :isSubmitting="addUserDialogData.isSubmitting"
      :roleOptions="rolesStore.hostRoleOptions"
      :disableNameFields="true"
      header="Invite Admin User"
      :canEditRole="false"
      @submit="onSubmitAddUser"
    >
      <WarningMessage
        class="mb-3"
        severity="secondary"
      >
        <!-- eslint-disable max-len -->
        A confirmation email will be sent to the address you provide. In the email, there will be a link where the user can navigate to set up their password. Please check the inbox (and spam folder, just in case) to find the invitation email.
        <!-- eslint-enable max-len -->
      </WarningMessage>
    </UserFormDialog>

    <UserFormDialog
      v-model:visible="editUserDialogData.visible"
      :isSubmitting="editUserDialogData.isSubmitting"
      :user="editUserDialogData.user"
      :roleOptions="rolesStore.hostRoleOptions"
      :disableNameFields="editUserDialogData.user && editUserDialogData.user.fname === null"
      @submit="onSubmitEditUser"
      :canEditRole="false"
    />

    <ConfirmDialog
      v-model:visible="deleteUserDialogData.visible"
      header="Confirm Delete"
      :isSubmitting="deleteUserDialogData.isSubmitting"
      @confirm="onSubmitDeleteUser"
    >
      <div v-html="deleteUserDialogData.body" />
    </ConfirmDialog>
  </PageContainer>
</template>

<script>
import { mapStores } from 'pinia';
import { object, string } from 'yup';
import { INVALID_FORM_SUBMISSION_MESSAGE } from '@/utils/messages';
import {
  useMyUserStore,
  useUsersStore,
  useRolesStore,
  useCategoriesStore,
} from '@/stores';
import * as api from '@/api';
import ConfirmDialog from '@/components/confirmDialog';
import AlertDialog from '@/components/alertDialog';
import UserFormDialog from '@/components/userFormDialog';
import { parseMessageFromError } from '@/utils/errors';

const USER_STATUS_ACTIVE = 'Active';
const USER_STATUS_PENDING = 'Pending';

export default {
  components: {
    UserFormDialog,
    ConfirmDialog,
  },
  computed: {
    ...mapStores(
      useMyUserStore,
      useUsersStore,
      useRolesStore,
      useCategoriesStore,
    ),
    compiledUsers() {
      const users = this.myUserStore.myUser && this.myUserStore.myUser.organization_id
        ? this.usersStore.getUsersByOrganizationId(this.myUserStore.myUser.organization_id)
        : [];

      const { roles } = this.rolesStore;

      return users.map((user) => {
        const matchingRole = roles.find((role) => Number(role.id) === Number(user.role_id));

        const status = user.fname !== null
          ? USER_STATUS_ACTIVE
          : USER_STATUS_PENDING;
        const statusChipClass = user.fname !== null
          ? 'bg-green-400 text-white'
          : 'bg-blue-100 text-blue-500';

        // remove user emails that were created without intention of being able to log in
        const sanitizedEmail = user.email.includes('@email.com')
          ? '-'
          : user.email;

        return {
          ...user,
          role: matchingRole
            ? matchingRole.name
            : 'Unknown',
          status,
          statusChipClass,
          sanitizedEmail,
        };
      });
    },
    userRowOptionsDictionary() {
      return this.compiledUsers.reduce((acc, item) => {
        acc[item.id] = [
          {
            label: 'Edit',
            command: () => this.onEditUser(item),
          },
          {
            label: 'Delete',
            command: () => this.onDeleteUser(item),
          },
        ];

        return acc;
      }, {});
    },
  },
  data() {
    return {
      schema: object({
        name: string().required('Name is required'),
      }),
      organizationForm: {
        name: '',
      },
      organizationFormIsSubmitting: false,
      addUserDialogData: {
        visible: false,
        isSubmitting: false,
      },
      editUserDialogData: {
        visible: false,
        isSubmitting: false,
        user: null,
      },
      deleteUserDialogData: {
        visible: false,
        isSubmitting: false,
        user: null,
      },
    };
  },
  mounted() {
    this.getOrganizationsUsers();
    this.rolesStore.getRoles();
    this.organizationForm = {
      name: this.myUserStore.myOrganization
        ? this.myUserStore.myOrganization.name
        : '',
    };
  },
  methods: {
    getOrganizationsUsers() {
      return this.usersStore.getUsers({
        organizationId: this.myUserStore.myOrganization
          ? this.myUserStore.myOrganization.id
          : undefined,
      });
    },
    onInvalidSubmit() {
      this.$toast.add({
        severity: 'warn',
        detail: INVALID_FORM_SUBMISSION_MESSAGE,
      });
    },
    async onSubmitOrganizationInfo() {
      try {
        this.organizationFormIsSubmitting = true;

        await api.updateOrganization({
          organizationId: this.myUserStore.myUser.organization_id,
          name: this.organizationForm.name,
        });
        await this.myUserStore.getMyUser();

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully updated organization',
        });
      } catch (error) {
        const message = parseMessageFromError(error, 'Error updating organization.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.organizationFormIsSubmitting = false;
      }
    },
    async onSubmitAddUser({ userForm }) {
      try {
        this.addUserDialogData.isSubmitting = true;

        const roleId = this.rolesStore.hostAdminRole.id;

        const invitedUser = await api.inviteUser({
          organizationId: this.myUserStore.myOrganization.id,
          email: userForm.email,
        });
        await api.updateUser({
          userId: invitedUser.user_id,
          roleId,
        });

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully invited user.',
        });

        this.addUserDialogData.visible = false;

        this.getOrganizationsUsers();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error inviting user.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.addUserDialogData.isSubmitting = false;
      }
    },
    onEditUser(user) {
      this.editUserDialogData = {
        ...this.editUserDialog,
        visible: true,
        user,
      };
    },
    async onSubmitEditUser({ user, userForm }) {
      try {
        this.editUserDialogData.isSubmitting = true;

        await api.updateUser({
          userId: user.id,
          firstName: userForm.firstName || '',
          lastName: userForm.lastName || '',
          email: userForm.email || '',
          roleId: userForm.roleId || undefined,
        });

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully updated user.',
        });

        this.editUserDialogData.visible = false;
        this.editUserDialogData.user = null;

        this.getOrganizationsUsers();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error updating user.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.editUserDialogData.isSubmitting = false;
      }
    },
    onDeleteUser(user) {
      const userIsSelf = user.id === this.myUserStore.myUser.id;
      if (userIsSelf) {
        this.$dialog.open(AlertDialog, {
          data: {
            text: 'You cannot remove yourself from the organization.',
          },
          props: {
            header: 'Alert',
            modal: true,
            dismissableMask: true,
            closable: true,
          },
        });
        return;
      }

      const isLastActiveUser = user.status === USER_STATUS_ACTIVE && this.compiledUsers
        .filter((item) => item.status === USER_STATUS_ACTIVE).length === 1;
      if (isLastActiveUser) {
        this.$dialog.open(AlertDialog, {
          data: {
            text: 'You cannot remove the last active user of an organization.',
          },
          props: {
            header: 'Alert',
            modal: true,
            dismissableMask: true,
            closable: true,
          },
        });
        return;
      }
      // TODO - can't delete admin user

      this.deleteUserDialogData = {
        ...this.deleteUserDialogData,
        visible: true,
        body: `<p>Are you sure you want to delete this user: <strong>${user.email}</strong>?</p>
          <p>This action cannot be undone.</p>`,
        user,
      };
    },
    async onSubmitDeleteUser() {
      try {
        this.deleteUserDialogData.isSubmitting = true;

        await api.deleteUser(this.deleteUserDialogData.user.id);

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully deleted user.',
        });

        this.deleteUserDialogData.visible = false;
        this.deleteUserDialogData.user = null;

        this.getOrganizationsUsers();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error deleting user.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.deleteUserDialogData.isSubmitting = false;
      }
    },
  },
};
</script>
